import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ConfirmDialog } from "@activecollab/components";
import PropTypes from "prop-types";
import { useFeatureIntent } from "~/repository/useFeatureIntent";
import { getOnDemandSettings } from "~/store/selectors/selector.configOptions";
import { getLoggedUserType } from "~/store/selectors/selector.users";
import { encodeData } from "~/utils/utils";

export const CallToUpgradeLink = ({ memberChildren, ownerChildren, page, context, features, href }) => {
  const { t } = useTranslation();
  const { mutate } = useFeatureIntent();

  const userClass = useSelector(getLoggedUserType);
  const onDemandSettings = useSelector(getOnDemandSettings).toJS();

  const callToUpgradeUrl = useMemo(() => {
    if (onDemandSettings?.current_plan?.name?.toUpperCase()?.includes("APP_SUMO")) {
      return features?.includes("additional_members")
        ? "/subscription/manage-add-ons/extra-members"
        : "/subscription/manage-add-ons";
    }
    return "/subscription/choose-plan";
  }, [features, onDemandSettings?.current_plan?.name]);
  const currentPlanName = useMemo(() => {
    return onDemandSettings?.current_plan?.verbose_name;
  }, [onDemandSettings?.current_plan]);

  const requestedFeatures = features?.join();
  const queryParams = requestedFeatures?.length
    ? `?${encodeData({
        "requested-features": requestedFeatures,
      })}`
    : "";

  const handleLogInfo = useCallback(() => {
    const data = {
      page,
      context,
      feature_name: requestedFeatures,
    };
    mutate(data);
  }, [context, mutate, page, requestedFeatures]);

  const [open, setOpen] = useState(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
    handleLogInfo();
  }, [handleLogInfo]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  if (userClass === "Client") return null;

  if (userClass === "Member") {
    return (
      <>
        {React.cloneElement(memberChildren, {
          onClick: handleOpen,
        })}
        <ConfirmDialog
          open={open}
          onCancel={handleClose}
          dialogTitle={t("Upgrade Your Plan")}
          dialogContent={t(
            `You are currently subscribed to our ${currentPlanName} Plan. Owners can upgrade plan and make this feature available.`
          )}
          confirmBtnText={t("Got it")}
          onConfirm={handleClose}
          shouldShowCancelButton={false}
        />
      </>
    );
  }

  return React.cloneElement(ownerChildren, {
    to: href ? `${href}${queryParams}` : `${callToUpgradeUrl}${queryParams}`,
    onClick: handleLogInfo,
  });
};

CallToUpgradeLink.propTypes = {
  memberChildren: PropTypes.node.isRequired,
  ownerChildren: PropTypes.node.isRequired,
  page: PropTypes.string.isRequired,
  context: PropTypes.string.isRequired,
  features: PropTypes.arrayOf(PropTypes.string).isRequired,
  href: PropTypes.string,
};
